import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import type { TeamId } from "../../types";
import { EMPLOYEE_COLORS, useTeams } from "../../stores";
import { useModalDrag } from "../../hooks/useModalDrag";

interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, color: string, team: TeamId, skills: string) => void;
}

export function AddEmployeeModal({ isOpen, onClose, onAdd }: AddEmployeeModalProps) {
  const { t } = useTranslation();
  const teams = useTeams();
  const [name, setName] = useState("");
  const [color, setColor] = useState(EMPLOYEE_COLORS[0]);
  const [team, setTeam] = useState<TeamId>(teams[0]?.id || "");
  const [skills, setSkills] = useState("");
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  const handleSubmit = () => {
    if (name.trim()) {
      onAdd(name.trim(), color, team, skills.trim());
      setName("");
      setColor(EMPLOYEE_COLORS[0]);
      setTeam(teams[0]?.id || "");
      setSkills("");
      onClose();
    }
  };

  const handleClose = () => {
    setName("");
    setColor(EMPLOYEE_COLORS[0]);
    setTeam(teams[0]?.id || "");
    setSkills("");
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          className={`bg-white rounded-lg shadow-lg w-full max-w-sm ${position ? '' : 'pointer-events-auto'}`}
          style={position ? { position: 'fixed', left: position.x, top: position.y, pointerEvents: 'auto' } : undefined}
        >
          <div
            className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <h3 className="text-[11px] font-medium text-gray-900">{t('addEmployeeModal.title')}</h3>
            <button onClick={handleClose} onMouseDown={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-3 py-2 space-y-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">{t('common.name')}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z.B. Max Mustermann"
                className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">{t('addEmployeeModal.teamLabel')}</label>
              <select
                value={team}
                onChange={(e) => setTeam(e.target.value as TeamId)}
                className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
              >
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">{t('common.color')}</label>
              <div className="flex flex-wrap gap-1">
                {EMPLOYEE_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-5 h-5 rounded-full transition-all ${
                      color === c ? "ring-2 ring-offset-1 ring-blue-500 scale-110" : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">{t('addEmployeeModal.skillsLabel')}</label>
              <input
                type="text"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="z.B. 🐘🐘👷👷🏗️"
                className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
              />
              <p className="mt-0.5 text-[9px] text-gray-400">{t('addEmployeeModal.skillsHint')}</p>
            </div>
          </div>
          <div className="px-3 py-1.5 border-t border-gray-200 flex items-center justify-between">
            <span className="text-[9px] text-gray-400">{`${t('common.escCancel')} · ${t('common.cmdEnterSave')}`}</span>
            <div className="flex gap-1.5">
              <button onClick={handleClose} className="px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-100 rounded transition-colors">
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!name.trim()}
                className="px-2 py-1 text-[10px] text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.add')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
